export const investmentAnalysisCourse = {
  id: "investment-analysis-portfolio-management-2024",
  title: "Investment Analysis and Portfolio Management: Complete Professional Course",
  shortDescription: "Master investment analysis, portfolio theory, and advanced portfolio management with real-world applications and hands-on modeling exercises.",
  description: "Comprehensive course covering investment analysis fundamentals, portfolio theory, asset allocation strategies, and professional portfolio management techniques used by institutional investors and wealth managers.",
  category: "Finance",
  industryCategory: "Financial Services",
  difficulty: "intermediate",
  duration: 20,
  estimatedWeeks: 10,
  instructorName: "Workazon Platform",
  instructorId: "platform",
  instructorBio: "Expert-curated content from certified investment professionals and portfolio managers",
  thumbnailUrl: "https://images.unsplash.com/photo-1560472355-536de3962603?w=400",
  price: 0,
  isPublic: true,
  status: "published",
  template: "comprehensive",
  version: "1.0",
  createdBy: "platform",
  createdAt: new Date(),
  lastModified: new Date(),
  updatedAt: new Date(),

  learningObjectives: [
    "Master fundamental and technical analysis techniques",
    "Build and optimize diversified investment portfolios",
    "Apply modern portfolio theory and CAPM models",
    "Evaluate risk-adjusted performance metrics",
    "Construct efficient frontier portfolios",
    "Implement professional asset allocation strategies",
    "Analyze alternative investments and hedge fund strategies",
    "Develop systematic investment processes and frameworks"
  ],

  prerequisites: [
    "Basic Statistics",
    "Financial Markets Knowledge",
    "Excel/Spreadsheet Skills"
  ],

  skills: [
    "Investment Analysis",
    "Portfolio Construction",
    "Risk Management",
    "Performance Attribution",
    "Asset Allocation",
    "Financial Modeling",
    "Quantitative Analysis",
    "Investment Research"
  ],

  tags: [
    "Investment Analysis",
    "Portfolio Management",
    "Asset Allocation",
    "Risk Analysis",
    "Performance Measurement",
    "Financial Modeling",
    "Quantitative Finance",
    "Institutional Investing"
  ],

  marketData: {
    averageSalary: 125000,
    salaryRange: {
      min: 85000,
      max: 200000
    },
    jobDemand: 4.5,
    marketTrend: "growing",
    careerPaths: [
      "Portfolio Manager",
      "Investment Analyst", 
      "Wealth Manager",
      "Asset Allocation Specialist",
      "Quantitative Analyst"
    ],
    relatedSkills: [
      "Bloomberg Terminal",
      "Python/R",
      "Financial Modeling",
      "Risk Management",
      "CFA Charter",
      "Portfolio Analytics"
    ]
  },

  enrollmentCount: 0,
  completionRate: 0,
  averageRating: 0,
  reviewCount: 0,

  modules: [
    {
      id: "investment-fundamentals",
      title: "Investment Fundamentals",
      description: "Core principles of investing, security types, and market mechanics",
      order: 1,
      estimatedHours: 3.5,
      content: [
        {
          id: "investment-basics",
          title: "Introduction to Investment Analysis",
          description: "Understanding investment vehicles, market structure, and analysis frameworks",
          type: "video",
          duration: 35
        },
        {
          id: "security-types",
          title: "Security Types and Characteristics",
          description: "Comprehensive overview of equity, fixed income, and alternative investments",
          type: "text",
          duration: 40,
          textContent: `# Security Types and Investment Characteristics

## Equity Securities

### Common Stock
- **Voting Rights**: Shareholders elect board of directors and vote on major corporate matters
- **Residual Claim**: Last in line for assets during liquidation, but unlimited upside potential
- **Dividend Rights**: Entitled to dividend payments when declared by board
- **Capital Appreciation**: Primary return driver through stock price increases

### Preferred Stock
- **Fixed Dividends**: Regular dividend payments, usually higher than common stock
- **Priority Claims**: Senior to common stock for dividends and liquidation
- **Limited Voting**: Typically no voting rights unless dividends are in arrears
- **Callable Features**: Company may redeem at predetermined price

## Fixed Income Securities

### Government Bonds
- **Treasury Bills**: Short-term (≤1 year), sold at discount to face value
- **Treasury Notes**: Medium-term (2-10 years), semi-annual coupon payments
- **Treasury Bonds**: Long-term (>10 years), semi-annual coupon payments
- **TIPS**: Treasury Inflation-Protected Securities, principal adjusts with inflation

### Corporate Bonds
- **Investment Grade**: Rated BBB-/Baa3 or higher, lower default risk
- **High Yield**: Rated below investment grade, higher yields but greater credit risk
- **Convertible Bonds**: Can be converted to common stock at predetermined ratio
- **Callable Bonds**: Issuer can redeem before maturity, typically at premium

### Municipal Bonds
- **General Obligation**: Backed by full faith and credit of issuing municipality
- **Revenue Bonds**: Backed by specific revenue stream (tolls, utilities)
- **Tax Advantages**: Interest typically exempt from federal taxes

## Alternative Investments

### Real Estate Investment Trusts (REITs)
- **Equity REITs**: Own and operate income-producing real estate
- **Mortgage REITs**: Provide financing for real estate purchases
- **Hybrid REITs**: Combination of equity and mortgage REITs
- **High Dividend Yields**: Required to distribute 90% of taxable income

### Commodities
- **Physical Commodities**: Agricultural products, metals, energy
- **Commodity Futures**: Contracts for future delivery of commodities
- **Commodity ETFs**: Exchange-traded funds tracking commodity indices
- **Inflation Hedge**: Often perform well during inflationary periods

### Private Equity and Hedge Funds
- **Private Equity**: Investments in private companies or buyouts of public companies
- **Hedge Funds**: Alternative investment strategies using leverage, derivatives
- **Venture Capital**: Early-stage investments in high-growth companies
- **Real Assets**: Infrastructure, natural resources, farmland

Understanding the characteristics and risk-return profiles of different securities is fundamental to effective portfolio construction and investment analysis.`
        },
        {
          id: "investment-process-interactive",
          title: "Investment Process Framework",
          description: "Build a systematic investment analysis framework",
          type: "finance-interactive",
          duration: 40,
          interactiveData: {
            exerciseType: "investment-process",
            instructions: "Design a comprehensive investment analysis process by prioritizing the key steps and selecting appropriate analysis techniques.",
            scenario: {
              processSteps: [
                "Define investment objectives and constraints",
                "Conduct macroeconomic analysis",
                "Perform industry sector analysis",
                "Execute individual security analysis",
                "Construct and optimize portfolio",
                "Implement investment decisions",
                "Monitor and rebalance portfolio",
                "Evaluate performance and attribution"
              ],
              analysisTools: [
                "Fundamental Analysis",
                "Technical Analysis", 
                "Quantitative Screening",
                "Risk Assessment",
                "Scenario Analysis",
                "Monte Carlo Simulation"
              ]
            },
            hints: [
              "Start with defining clear investment objectives",
              "Top-down analysis moves from macro to micro",
              "Consider both quantitative and qualitative factors",
              "Implementation and monitoring are crucial final steps"
            ]
          }
        }
      ],
      quiz: {
        id: "investment-fundamentals-quiz",
        title: "Investment Fundamentals Quiz",
        description: "Test your knowledge of investment basics and security types",
        timeLimit: 15,
        passingScore: 80,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "Which type of security has the highest claim priority during bankruptcy?",
            options: [
              "Common Stock",
              "Preferred Stock", 
              "Corporate Bonds",
              "Convertible Bonds"
            ],
            correctAnswer: 2,
            explanation: "Corporate bonds are debt instruments and have higher priority than equity securities (common and preferred stock) during bankruptcy proceedings."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "What is the primary advantage of Treasury Inflation-Protected Securities (TIPS)?",
            options: [
              "Higher yield than regular Treasury bonds",
              "Protection against inflation risk",
              "Tax-free interest payments",
              "Shorter maturity periods"
            ],
            correctAnswer: 1,
            explanation: "TIPS provide protection against inflation by adjusting the principal amount based on changes in the Consumer Price Index."
          }
        ]
      }
    },

    {
      id: "portfolio-theory",
      title: "Modern Portfolio Theory",
      description: "Markowitz portfolio theory, efficient frontier, and optimal portfolio construction",
      order: 2,
      estimatedHours: 4.5,
      content: [
        {
          id: "markowitz-theory",
          title: "Markowitz Portfolio Theory",
          description: "Understanding risk-return optimization and diversification benefits",
          type: "text",
          duration: 50,
          textContent: `# Modern Portfolio Theory

## Harry Markowitz Framework

Modern Portfolio Theory (MPT), developed by Harry Markowitz in 1952, revolutionized investment management by introducing mathematical rigor to portfolio construction. The theory provides a framework for constructing portfolios that maximize expected return for a given level of risk.

### Key Assumptions

**Rational Investors**
- Investors are risk-averse and prefer higher returns to lower returns
- Given the same expected return, investors prefer lower risk
- Utility function exhibits diminishing marginal utility of wealth

**Market Efficiency**
- All investors have access to the same information
- No transaction costs or taxes
- Assets are infinitely divisible
- Single investment period

**Statistical Properties**
- Asset returns follow normal distribution
- Expected returns, variances, and correlations are known and constant
- Investors make decisions based on expected return and variance only

## Mathematical Foundation

### Expected Portfolio Return
E(Rp) = Σ wi × E(Ri)

Where:
- E(Rp) = Expected portfolio return
- wi = Weight of asset i in portfolio
- E(Ri) = Expected return of asset i

### Portfolio Variance
σ²p = Σ Σ wi × wj × σij

Where:
- σ²p = Portfolio variance
- σij = Covariance between assets i and j
- For i = j, σij = σ²i (variance of asset i)

### Diversification Benefits

**Risk Reduction Through Correlation**
- Perfect positive correlation (ρ = +1): No diversification benefit
- Zero correlation (ρ = 0): Significant risk reduction possible
- Perfect negative correlation (ρ = -1): Maximum risk reduction, possible to eliminate risk

**Portfolio Standard Deviation (Two Assets)**
σp = √[w₁²σ₁² + w₂²σ₂² + 2w₁w₂σ₁σ₂ρ₁₂]

## Efficient Frontier

### Concept and Construction
The efficient frontier represents the set of optimal portfolios offering the highest expected return for each level of risk. Portfolios below the frontier are sub-optimal, while portfolios above are unattainable.

**Minimum Variance Portfolio**
- Leftmost point on efficient frontier
- Lowest possible portfolio risk
- May not provide optimal risk-return trade-off for all investors

**Tangent Portfolio (Market Portfolio)**
- Portfolio with highest Sharpe ratio
- Optimal risky portfolio when combined with risk-free asset
- Forms Capital Allocation Line (CAL)

### Capital Allocation Line
The CAL represents combinations of risk-free asset and optimal risky portfolio:

E(Rc) = Rf + (E(Rm) - Rf) × (σc/σm)

Where:
- E(Rc) = Expected return of complete portfolio
- Rf = Risk-free rate
- E(Rm) = Expected return of market portfolio
- σc = Standard deviation of complete portfolio
- σm = Standard deviation of market portfolio

## Practical Applications

### Portfolio Optimization Process

**Step 1: Define Investment Universe**
- Select asset classes and individual securities
- Determine investability constraints
- Consider liquidity requirements

**Step 2: Estimate Parameters**
- Historical return analysis
- Forward-looking return expectations
- Correlation and covariance estimation
- Risk model specification

**Step 3: Apply Constraints**
- Minimum/maximum position sizes
- Sector or geographic limits
- ESG considerations
- Liquidity requirements

**Step 4: Solve Optimization**
- Quadratic programming solution
- Risk budgeting approach
- Black-Litterman model adjustments

### Limitations and Criticisms

**Input Sensitivity**
- Small changes in expected returns can dramatically alter optimal weights
- Correlation estimates are unstable over time
- Historical data may not predict future relationships

**Practical Challenges**
- Transaction costs ignored
- Single-period framework oversimplifies reality
- Normal distribution assumption often violated
- Estimation error can lead to poorly diversified portfolios

**Behavioral Considerations**
- Investors may not be purely rational
- Home bias and familiarity bias
- Mental accounting affects portfolio decisions

Despite its limitations, Modern Portfolio Theory remains the foundation of professional portfolio management and has spawned numerous extensions and improvements including Black-Litterman model, risk parity, and factor investing approaches.`
        },
        {
          id: "efficient-frontier-builder",
          title: "Efficient Frontier Construction",
          description: "Build efficient portfolios using mean-variance optimization",
          type: "finance-interactive",
          duration: 60,
          interactiveData: {
            exerciseType: "efficient-frontier",
            instructions: "Construct an efficient frontier by optimizing portfolio weights across different risk levels. Analyze the risk-return trade-offs and identify the optimal portfolio.",
            scenario: {
              assets: [
                { symbol: "STOCKS", expectedReturn: 0.10, volatility: 0.16, name: "Stock Index" },
                { symbol: "BONDS", expectedReturn: 0.04, volatility: 0.05, name: "Bond Index" },
                { symbol: "REITS", expectedReturn: 0.08, volatility: 0.18, name: "REITs Index" },
                { symbol: "COMMODITIES", expectedReturn: 0.06, volatility: 0.22, name: "Commodities" }
              ],
              correlationMatrix: [
                [1.00, 0.20, 0.60, 0.30],
                [0.20, 1.00, 0.10, -0.10],
                [0.60, 0.10, 1.00, 0.40],
                [0.30, -0.10, 0.40, 1.00]
              ],
              riskFreeRate: 0.02
            },
            hints: [
              "Start by calculating portfolio return and risk for different weight combinations",
              "The efficient frontier curves upward and to the right",
              "Look for the portfolio with the highest Sharpe ratio",
              "Consider adding the risk-free asset to improve the opportunity set"
            ]
          }
        }
      ],
      quiz: {
        id: "portfolio-theory-quiz",
        title: "Modern Portfolio Theory Quiz",
        description: "Test your understanding of MPT concepts and applications",
        timeLimit: 20,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "According to Modern Portfolio Theory, what happens to portfolio risk when you combine two perfectly negatively correlated assets?",
            options: [
              "Risk increases significantly",
              "Risk remains unchanged",
              "Risk can be completely eliminated",
              "Risk decreases but cannot reach zero"
            ],
            correctAnswer: 2,
            explanation: "With perfect negative correlation (ρ = -1), it's theoretically possible to construct a portfolio with zero risk by choosing appropriate weights."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "What does the tangent portfolio represent on the efficient frontier?",
            options: [
              "The minimum variance portfolio",
              "The portfolio with highest expected return",
              "The portfolio with highest Sharpe ratio",
              "The equally weighted portfolio"
            ],
            correctAnswer: 2,
            explanation: "The tangent portfolio is the point where the Capital Allocation Line touches the efficient frontier, representing the portfolio with the highest risk-adjusted return (Sharpe ratio)."
          }
        ]
      }
    },

    {
      id: "asset-allocation",
      title: "Strategic Asset Allocation",
      description: "Long-term asset allocation strategies and implementation frameworks",
      order: 3,
      estimatedHours: 4.0,
      content: [
        {
          id: "allocation-frameworks",
          title: "Asset Allocation Frameworks",
          description: "Strategic vs tactical allocation and implementation approaches",
          type: "video",
          duration: 45
        },
        {
          id: "lifecycle-investing",
          title: "Lifecycle and Target-Date Investing",
          description: "Age-based asset allocation and glide path strategies",
          type: "text",
          duration: 35,
          textContent: `# Lifecycle and Target-Date Investing

## Lifecycle Investment Theory

Lifecycle investing recognizes that optimal asset allocation changes as investors age due to evolving risk tolerance, time horizon, and financial circumstances. This approach forms the foundation of target-date funds and age-based portfolio strategies.

### Theoretical Foundation

**Human Capital Considerations**
- Young investors have substantial human capital (future earnings)
- Human capital acts as bond-like asset (stable income stream)
- As human capital decreases with age, financial assets should become more conservative

**Time Horizon Effects**
- Longer time horizons allow for more risk-taking
- Time diversification benefits (though debated in academic literature)
- Ability to recover from market downturns decreases with age

**Changing Risk Tolerance**
- Risk capacity generally decreases as retirement approaches
- Need for portfolio income increases in retirement
- Sequence of returns risk becomes critical near retirement

## Traditional Age-Based Rules

### "100 Minus Age" Rule
**Formula**: Stock Allocation % = 100 - Age
- 30-year-old: 70% stocks, 30% bonds
- 50-year-old: 50% stocks, 50% bonds  
- 70-year-old: 30% stocks, 70% bonds

**Modern Variations**:
- "110 Minus Age" or "120 Minus Age" (reflecting longer lifespans)
- Accounts for increased life expectancy and longer retirement periods

### Limitations of Simple Rules
- Doesn't account for individual circumstances
- Ignores other asset classes beyond stocks and bonds
- May be too conservative for modern retirement needs
- Fails to consider changing market conditions

## Target-Date Fund Strategies

### Glide Path Design

**Traditional Glide Path**
- High equity allocation in early years (80-90%)
- Gradual reduction in equity exposure approaching retirement
- Continued de-risking through retirement years
- Final allocation around 30-40% equity

**Modern Innovations**
- **Through vs. To Retirement**: Glide paths continue evolving after retirement
- **Rising Equity Glide Paths**: Some funds increase equity in early retirement
- **Multi-Asset Diversification**: Include REITs, commodities, international exposure

### Implementation Approaches

**Static vs. Dynamic Allocation**
- Static: Fixed glide path regardless of market conditions
- Dynamic: Adjusts based on market valuations and volatility
- Volatility management overlays

**Risk Budgeting**
- Volatility targeting (e.g., maintain 12% portfolio volatility)
- Downside risk management
- Behavioral risk considerations

## Advanced Lifecycle Strategies

### Bucket Strategy
**Concept**: Divide portfolio into time-based buckets
- **Bucket 1 (0-5 years)**: Conservative investments for immediate needs
- **Bucket 2 (5-15 years)**: Moderate growth investments
- **Bucket 3 (15+ years)**: Aggressive growth investments

**Benefits**:
- Behavioral comfort during market stress
- Allows for different investment strategies by time horizon
- Reduces sequence of returns risk

### Glidepath Variations

**U-Shaped Equity Exposure**
- High equity early in career
- Reduction approaching retirement
- Increase in early retirement (when portfolio is larger)

**Bond Tent Strategy**
- Dramatically reduce equity exposure 5-10 years before retirement
- Gradually increase equity post-retirement
- Protects against sequence risk around retirement date

### Factor-Based Lifecycle Investing

**Value Tilt for Older Investors**
- Value stocks may provide better downside protection
- Dividend-focused strategies for income generation
- Quality factor exposure reduces volatility

**Growth Tilt for Younger Investors**
- Growth stocks benefit from long time horizons
- Small-cap exposure for higher expected returns
- International diversification for broader opportunities

## Implementation Considerations

### Rebalancing Strategies
- **Calendar Rebalancing**: Fixed schedule (annually, quarterly)
- **Threshold Rebalancing**: Rebalance when allocation drifts beyond bands
- **Tactical Adjustments**: Opportunistic rebalancing during market stress

### Tax Efficiency
- Asset location optimization (tax-deferred vs. taxable accounts)
- Tax-loss harvesting opportunities
- Roth conversion strategies coordinated with asset allocation

### Behavioral Aspects
- Automation reduces behavioral mistakes
- Clear communication of strategy rationale
- Regular reviews and adjustments for life changes

Lifecycle investing provides a systematic framework for evolving asset allocation, but should be customized based on individual circumstances, risk tolerance, and financial goals.`
        },
        {
          id: "asset-allocation-optimizer",
          title: "Asset Allocation Optimization Tool",
          description: "Build optimal asset allocation based on investor profile and constraints",
          type: "finance-interactive",
          duration: 50,
          interactiveData: {
            exerciseType: "asset-allocation",
            instructions: "Design an optimal asset allocation strategy by considering investor profile, market conditions, and investment constraints. Balance risk and return objectives.",
            scenario: {
              investorProfile: {
                age: 35,
                riskTolerance: "moderate",
                timeHorizon: 30,
                incomeStability: "stable",
                currentWealth: 500000
              },
              assetClasses: [
                { name: "US Large Cap", expectedReturn: 0.09, volatility: 0.15, minWeight: 0.2, maxWeight: 0.5 },
                { name: "US Small Cap", expectedReturn: 0.11, volatility: 0.20, minWeight: 0.0, maxWeight: 0.2 },
                { name: "International", expectedReturn: 0.08, volatility: 0.18, minWeight: 0.1, maxWeight: 0.3 },
                { name: "Bonds", expectedReturn: 0.04, volatility: 0.04, minWeight: 0.2, maxWeight: 0.6 },
                { name: "REITs", expectedReturn: 0.07, volatility: 0.19, minWeight: 0.0, maxWeight: 0.15 },
                { name: "Commodities", expectedReturn: 0.05, volatility: 0.21, minWeight: 0.0, maxWeight: 0.10 }
              ]
            },
            hints: [
              "Consider the investor's age and risk tolerance",
              "Ensure adequate diversification across asset classes",
              "Balance growth potential with downside protection",
              "Account for the long time horizon in the allocation"
            ]
          }
        }
      ]
    },

    {
      id: "performance-measurement",
      title: "Performance Measurement & Attribution",
      description: "Portfolio performance evaluation, benchmarking, and attribution analysis",
      order: 4,
      estimatedHours: 4.0,
      content: [
        {
          id: "performance-metrics",
          title: "Risk-Adjusted Performance Measures",
          description: "Sharpe ratio, alpha, beta, and advanced performance metrics",
          type: "text",
          duration: 45,
          textContent: `# Risk-Adjusted Performance Measurement

## Traditional Performance Metrics

### Absolute Return Measures
**Simple Return**: (Ending Value - Beginning Value) / Beginning Value
**Time-Weighted Return**: Geometric mean return eliminating impact of cash flows
**Money-Weighted Return**: Internal rate of return including cash flow timing effects

**Annualized Return Calculation**:
Annual Return = (1 + Period Return)^(365/Days) - 1

### Risk Measures
**Standard Deviation**: Measure of return volatility
**Beta**: Systematic risk relative to market benchmark
**Tracking Error**: Standard deviation of excess returns vs. benchmark

## Risk-Adjusted Performance Ratios

### Sharpe Ratio
**Formula**: (Rp - Rf) / σp
- Measures excess return per unit of total risk
- Higher values indicate better risk-adjusted performance
- Assumes normal return distribution

**Interpretation**:
- > 1.0: Excellent risk-adjusted performance
- 0.5 - 1.0: Good performance
- < 0.5: Poor risk-adjusted returns

### Information Ratio
**Formula**: (Rp - Rb) / TE
- Measures active return per unit of tracking error
- Evaluates manager skill in generating alpha
- Higher values indicate consistent outperformance

**Benchmark**: > 0.5 considered good active management

### Treynor Ratio
**Formula**: (Rp - Rf) / βp
- Measures excess return per unit of systematic risk
- Useful for well-diversified portfolios
- Complements Sharpe ratio analysis

### Jensen's Alpha
**Formula**: αp = Rp - [Rf + βp(Rm - Rf)]
- Measures risk-adjusted excess return over CAPM prediction
- Positive alpha indicates outperformance
- Statistical significance testing important

## Advanced Performance Measures

### Sortino Ratio
**Formula**: (Rp - MAR) / DD
- Uses downside deviation instead of standard deviation
- MAR = Minimum Acceptable Return
- Better for asymmetric return distributions

### Calmar Ratio
**Formula**: Annual Return / Maximum Drawdown
- Focuses on worst-case scenario performance
- Popular in hedge fund evaluation
- Higher values indicate better downside protection

### Omega Ratio
**Formula**: Probability-weighted gains / Probability-weighted losses
- Considers entire return distribution
- Threshold return separates gains from losses
- Accounts for higher moments (skewness, kurtosis)

## Performance Attribution Analysis

### Brinson-Hood-Beebower Model
**Total Return Attribution**:
Total Excess Return = Asset Allocation Effect + Security Selection Effect + Interaction Effect

**Asset Allocation Effect**:
Σ(wpi - wbi) × Rbi

**Security Selection Effect**:
Σwbi × (Rpi - Rbi)

Where:
- wpi = Portfolio weight in sector i
- wbi = Benchmark weight in sector i  
- Rpi = Portfolio return in sector i
- Rbi = Benchmark return in sector i

### Factor-Based Attribution

**Multi-Factor Models**:
Rp - Rf = α + β₁F₁ + β₂F₂ + ... + βnFn + ε

Common factors:
- **Style Factors**: Value, Growth, Quality, Momentum
- **Size Factors**: Large-cap, Small-cap exposure
- **Sector Factors**: Technology, Healthcare, Financial exposure
- **Geographic Factors**: Domestic, International, Emerging Markets

### Currency Attribution (International Portfolios)
**Total Return Decomposition**:
Total Return = Local Market Return + Currency Return + Cross-Product

**Hedging Analysis**:
- Unhedged return components
- Hedging cost/benefit analysis
- Optimal hedge ratio determination

## Performance Evaluation Best Practices

### Benchmark Selection Criteria
**Appropriate Benchmark Characteristics**:
- Unambiguous composition
- Investable securities
- Measurable performance
- Reflective of manager's investment universe
- Specified in advance

### Statistical Significance Testing
**T-Statistics for Alpha**:
t = α / (σα)
- Evaluate whether outperformance is statistically meaningful
- Consider sample size and time period
- Account for multiple testing issues

### Performance Persistence Analysis
**Tracking Manager Consistency**:
- Rolling period performance analysis
- Up/down market analysis
- Style consistency evaluation
- Risk management effectiveness

## Behavioral Considerations

### Performance Presentation Bias
**Cherry-Picking**: Selective time period reporting
**Survivorship Bias**: Only showing successful funds
**Backfill Bias**: Including historical performance post-launch

### Performance Evaluation Framework
**Multi-Dimensional Analysis**:
1. Return analysis (absolute and relative)
2. Risk analysis (multiple measures)
3. Attribution analysis (sources of return)
4. Consistency analysis (performance persistence)
5. Process evaluation (investment approach)

Effective performance measurement goes beyond simple return calculations to provide insight into the sources of performance, consistency of results, and quality of investment management processes.`
        },
        {
          id: "performance-attribution-tool",
          title: "Performance Attribution Analysis",
          description: "Analyze portfolio performance and identify sources of returns",
          type: "finance-interactive",
          duration: 55,
          interactiveData: {
            exerciseType: "performance-attribution",
            instructions: "Conduct comprehensive performance attribution analysis by decomposing portfolio returns into asset allocation, security selection, and interaction effects.",
            scenario: {
              portfolioReturns: {
                "Technology": { weight: 0.30, return: 0.15 },
                "Healthcare": { weight: 0.20, return: 0.12 },
                "Financials": { weight: 0.25, return: 0.08 },
                "Consumer": { weight: 0.15, return: 0.10 },
                "Energy": { weight: 0.10, return: 0.05 }
              },
              benchmarkReturns: {
                "Technology": { weight: 0.25, return: 0.13 },
                "Healthcare": { weight: 0.15, return: 0.11 },
                "Financials": { weight: 0.30, return: 0.09 },
                "Consumer": { weight: 0.20, return: 0.08 },
                "Energy": { weight: 0.10, return: 0.06 }
              }
            },
            hints: [
              "Calculate asset allocation effect: (Portfolio Weight - Benchmark Weight) × Benchmark Return",
              "Calculate security selection effect: Benchmark Weight × (Portfolio Return - Benchmark Return)",
              "Sum all effects to get total attribution",
              "Positive values indicate value-added performance"
            ]
          }
        }
      ]
    },

    {
      id: "alternative-investments",
      title: "Alternative Investments",
      description: "Private equity, hedge funds, real estate, and alternative investment strategies",
      order: 5,
      estimatedHours: 4.0,
      content: [
        {
          id: "private-equity",
          title: "Private Equity and Venture Capital",
          description: "Understanding private equity structures, valuation, and return profiles",
          type: "video",
          duration: 50
        },
        {
          id: "hedge-fund-strategies",
          title: "Hedge Fund Strategies and Analysis",
          description: "Overview of hedge fund strategies and due diligence frameworks",
          type: "text",
          duration: 40,
          textContent: `# Hedge Fund Strategies and Analysis

## Hedge Fund Overview

### Definition and Characteristics
Hedge funds are privately organized investment vehicles that use sophisticated strategies and instruments to generate returns for qualified investors. They typically have more flexibility than traditional investment managers in terms of:

- **Investment Strategies**: Long/short positions, derivatives, leverage
- **Asset Classes**: Stocks, bonds, currencies, commodities, private investments
- **Geographic Scope**: Global investment opportunities
- **Regulatory Framework**: Less regulated than mutual funds (accredited investors only)

### Common Structure Features
- **Limited Partnership**: General partner (GP) manages, limited partners (LPs) invest
- **High Water Mark**: Prevents double charging of performance fees
- **Hurdle Rate**: Minimum return before performance fees apply
- **Lock-up Periods**: Restrictions on investor withdrawals (typically 1-3 years)

## Major Hedge Fund Strategies

### Equity Strategies

**Long/Short Equity**
- **Approach**: Buy undervalued stocks, sell overvalued stocks
- **Market Exposure**: Net long or market neutral positions
- **Risk Management**: Sector, geographic, and factor diversification
- **Return Drivers**: Security selection skill, market timing

**Market Neutral**
- **Objective**: Generate returns independent of market direction
- **Implementation**: Equal dollar amounts long and short
- **Risk Sources**: Stock selection, sector rotation, factor exposure
- **Beta Target**: Near zero market beta

**Equity Long Bias**
- **Strategy**: Primarily long positions with some short hedging
- **Market Exposure**: Net long exposure (typically 30-70%)
- **Return Profile**: Correlation with equity markets but with alpha generation
- **Downside Protection**: Short positions provide some hedge

### Fixed Income Strategies

**Fixed Income Arbitrage**
- **Approach**: Exploit pricing inefficiencies in bond markets
- **Common Trades**: Yield curve arbitrage, swap spread trades, basis trades
- **Leverage**: High leverage to amplify small price differences
- **Risk Management**: Duration, credit, and liquidity risk controls

**Distressed Securities**
- **Focus**: Companies in financial distress or bankruptcy
- **Investment Horizon**: Longer-term positions (1-3 years)
- **Return Sources**: Corporate restructuring, asset sales, operational improvements
- **Expertise Required**: Legal, accounting, and operational turnaround knowledge

### Event-Driven Strategies

**Merger Arbitrage**
- **Strategy**: Buy target company, short acquirer (if stock deal)
- **Return Source**: Deal spread compression
- **Risks**: Deal break risk, timing uncertainty, regulatory approval
- **Typical Returns**: 6-10% annually with low correlation to markets

**Activist Investing**
- **Approach**: Take significant positions and influence management
- **Catalysts**: Board representation, strategic changes, capital allocation
- **Time Horizon**: Medium to long-term (1-5 years)
- **Return Potential**: High returns but concentrated positions

### Relative Value Strategies

**Convertible Bond Arbitrage**
- **Strategy**: Long convertible bonds, short underlying stock
- **Hedge Ratios**: Dynamic hedging based on option sensitivity
- **Return Sources**: Credit spread tightening, volatility premium capture
- **Risk Factors**: Interest rate changes, credit migration, liquidity

**Statistical Arbitrage**
- **Methodology**: Quantitative models identify pricing relationships
- **Implementation**: Mean reversion, momentum, factor models
- **Time Horizon**: Short-term (days to weeks)
- **Technology**: High-frequency trading, algorithmic execution

### Macro Strategies

**Global Macro**
- **Scope**: Currency, interest rate, commodity, and equity index positions
- **Analysis**: Top-down macroeconomic and political analysis
- **Instruments**: Futures, options, forwards, swaps
- **Diversification**: Multiple uncorrelated themes and geographies

**Managed Futures (CTA)**
- **Approach**: Systematic trend-following strategies
- **Markets**: Futures contracts across all asset classes
- **Models**: Technical analysis, momentum, mean reversion
- **Benefits**: Crisis alpha, portfolio diversification

## Due Diligence Framework

### Investment Process Evaluation

**Strategy Assessment**
- Competitive advantages and differentiation
- Historical performance across market cycles
- Capacity constraints and scalability
- Risk management framework

**Team Analysis**
- Track record and experience
- Key person risk assessment
- Organizational stability
- Succession planning

### Operational Due Diligence

**Risk Management**
- Independent risk oversight
- Position sizing and concentration limits
- Stress testing and scenario analysis
- Liquidity risk management

**Operations and Controls**
- Trade execution and settlement
- Valuation policies and procedures
- Compliance and regulatory oversight
- Technology infrastructure

### Legal and Structural Analysis

**Terms and Conditions**
- Management and performance fees
- Redemption terms and gates
- Side pocket provisions
- Key person and investment restrictions

**Service Provider Quality**
- Prime brokerage relationships
- Auditing and accounting
- Legal counsel and compliance
- Administrator capabilities

## Portfolio Construction with Hedge Funds

### Allocation Frameworks

**Core-Satellite Approach**
- Core: Diversified hedge fund portfolio
- Satellite: Specialized strategies or emerging managers
- Benefits: Broad diversification with targeted alpha opportunities

**Risk Budgeting**
- Volatility-based allocation
- Maximum drawdown constraints
- Correlation-adjusted sizing
- Factor exposure management

### Multi-Manager Strategies

**Fund of Funds Benefits**
- Professional due diligence
- Diversification across strategies
- Risk management overlay
- Operational efficiency

**Direct Investment Advantages**
- Lower fees (no fund of funds layer)
- Better transparency
- Customization opportunities
- Direct manager relationships

Hedge funds can provide valuable portfolio diversification and return enhancement, but require sophisticated analysis and ongoing monitoring to justify their complexity and fees.`
        }
      ]
    }
  ],

  assignments: [
    {
      id: "portfolio-construction-project",
      title: "Comprehensive Portfolio Construction Project",
      description: "Build and analyze a diversified investment portfolio using modern portfolio theory and professional investment processes",
      type: "project",
      dueWeeks: 6,
      maxScore: 100,
      instructions: `# Portfolio Construction Project

## Objective
Construct a comprehensive investment portfolio for a specific investor profile and conduct thorough analysis of the resulting portfolio characteristics.

## Requirements
1. **Investor Profile**: Define detailed investor characteristics (age, goals, constraints)
2. **Asset Allocation**: Apply strategic asset allocation principles
3. **Security Selection**: Select specific securities within each asset class
4. **Risk Analysis**: Conduct comprehensive risk assessment
5. **Performance Projections**: Model expected returns and scenarios
6. **Implementation Plan**: Develop practical implementation strategy

## Deliverables
- Executive summary with investment recommendation
- Detailed portfolio analysis report (20-25 pages)
- Excel model with optimization calculations
- Risk assessment and stress testing results
- Implementation timeline and monitoring framework

## Evaluation Criteria
- Theoretical foundation and methodology (25%)
- Analytical rigor and calculations (30%)
- Practical considerations and implementation (25%)
- Communication and presentation (20%)`,
      submissionFormat: "pdf",
      resources: [
        "Portfolio optimization software tutorial",
        "Case studies in asset allocation",
        "Performance measurement guidelines"
      ]
    }
  ],

  finalExam: {
    id: "investment-analysis-final",
    title: "Investment Analysis and Portfolio Management Final Exam",
    description: "Comprehensive examination of investment analysis concepts, portfolio theory, and professional practices",
    timeLimit: 120,
    passingScore: 80,
    attempts: 2,
    questions: [
      {
        id: "final-q1",
        type: "multiple-choice",
        question: "According to the Capital Asset Pricing Model (CAPM), what determines the expected return of a security?",
        options: [
          "Only the risk-free rate and market return",
          "The risk-free rate, market return, and the security's beta",
          "The security's alpha and correlation with the market",
          "The security's dividend yield and growth rate"
        ],
        correctAnswer: 1,
        explanation: "CAPM states that expected return equals the risk-free rate plus beta times the market risk premium: E(R) = Rf + β(Rm - Rf)."
      },
      {
        id: "final-q2",
        type: "multiple-choice",
        question: "What is the primary benefit of portfolio rebalancing?",
        options: [
          "Maximizing returns through market timing",
          "Maintaining target asset allocation and risk profile",
          "Minimizing transaction costs",
          "Avoiding taxable events"
        ],
        correctAnswer: 1,
        explanation: "Rebalancing maintains the portfolio's intended risk-return characteristics by restoring target asset allocation weights that drift over time due to different asset class performance."
      },
      {
        id: "final-q3",
        type: "multiple-choice",
        question: "Which performance measure is most appropriate for evaluating actively managed portfolios?",
        options: [
          "Absolute return",
          "Sharpe ratio",
          "Information ratio",
          "Beta"
        ],
        correctAnswer: 2,
        explanation: "The Information ratio measures active return per unit of tracking error, making it ideal for evaluating active management skill relative to a benchmark."
      }
    ]
  },

  quizzes: []
};